import { Box, Typography, Card, useMediaQuery, Button } from "@mui/material";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../../components/LoadingGif";
import { useState, useEffect, } from "react";
import { useRoom } from "../../context/RoomContext";

const RoomManagementTab = ({
  bookings,
  setBookings,
  publishedRooms,
  displayOccupants,
  displaySchedule,
  handleOpenModal,
  setReviewBookings
}) => {
  const { t } = useTranslation();
  const { ListRoomBookings, loading, } = useRoom();
  const isMobile = useMediaQuery("(max-width:1024px)");
  const [filter, setFilter] = useState("");
  const [filteredRooms, setFilteredRooms] = useState([]);

  useEffect(() => {
    const fetchFilteredBookings = async () => {
      setFilteredRooms(publishedRooms.data || []);
      const targetRooms = [...new Set(publishedRooms.data.map((room) => room.roomId))];

      const params = { page: 1, status: "tenants" };

      const results = await Promise.all(
        targetRooms.map((room) => ListRoomBookings(room, params))
      );

      const allBookings = results.flatMap((result) => result?.data || result || []);
      const maxPage = Math.max(...results.map((result) => result.pagination?.totalPages || 1), 1);

      if (maxPage > 1) {
        const allPagesResults = await Promise.all(
          Array.from({ length: maxPage - 1 }, (_, i) => i + 2).flatMap((page) =>
            targetRooms.map((room) => ListRoomBookings(room, { ...params, page }))
          )
        );
        const additionalBookings = allPagesResults.flatMap((result) => result?.data || result || []);
        allBookings.push(...additionalBookings);
      }

      const confirmedParams = { page: 1, status: "confirmed" };
      const confirmedResults = await Promise.all(
        targetRooms.map((room) => ListRoomBookings(room, confirmedParams))
      );

      const confirmedBookings = confirmedResults.flatMap((result) => result?.data || result || []);
      const confirmedMaxPage = Math.max(...confirmedResults.map((result) => result.pagination?.totalPages || 1), 1);

      if (confirmedMaxPage > 1) {
        const confirmedPagesResults = await Promise.all(
          Array.from({ length: confirmedMaxPage - 1 }, (_, i) => i + 2).flatMap((page) =>
            targetRooms.map((room) => ListRoomBookings(room, { ...confirmedParams, page }))
          )
        );
        const additionalConfirmedBookings = confirmedPagesResults.flatMap((result) => result?.data || result || []);
        confirmedBookings.push(...additionalConfirmedBookings);
      }

      allBookings.push(...confirmedBookings);
      setBookings(allBookings || []);
    };
    fetchFilteredBookings();
  }, [ListRoomBookings, publishedRooms]);

  useEffect(() => {
    if (filter) {
      const filteredRooms = publishedRooms.data.filter((room) => room.availability === filter);
      setFilteredRooms(filteredRooms);
    } else {
      setFilteredRooms(publishedRooms.data || []);
    }
  }, [filter, publishedRooms, bookings]);

  const filterButtonStyle = {
    borderRadius: "20px",
    padding: "8px 16px",
    textTransform: "none",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    border: "1px solid #E5E7EB",
    cursor: "pointer",
  };

  return (
    loading ? (
      <LoadingGif />
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%", }}>
        <Box sx={{ display: "flex", gap: "1%", }}>
          <Card
            sx={{
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              width: "33%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box
                component="img"
                src="/icons/roomAvailable.svg"
                alt="roomAvailable"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#00A63E" }}>
                  {publishedRooms.available}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {t("Available")}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card
            sx={{
              width: "33%",
              flexDirection: "column",
              gap: 2,
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src="/icons/roomReserved.svg"
                alt="roomReserved"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#FFB60D" }}>
                  {publishedRooms.reserved}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {t("Reserved")}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card
            sx={{
              width: "33%",
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src="/icons/roomOccupied.svg"
                alt="roomOccupied"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#FF6900" }}>
                  {publishedRooms.occupied}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
                  {t("Occupied")}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 2, }}>
          <Typography sx={{ fontSize: "14px", color: "#4A5565", }}>
            {t("Filter")}:
          </Typography>
          <Button
            onClick={() => { setFilter("available"); }}
            sx={{
              ...filterButtonStyle,
              backgroundColor: filter === "available" ? "#00A63E" : "#fff",
              color: filter === "available" ? "#fff" : "#000",

            }}
          >
            <Box
              component="img"
              src="/icons/roomAvailable.svg"
              alt="roomAvailable"
              sx={{ width: "20px", height: "auto" }}
            />
            <div>{t('Available')}</div>
          </Button>
          <Button
            onClick={() => { setFilter("reserved"); }}
            sx={{
              ...filterButtonStyle,
              backgroundColor: filter === "reserved" ? "#FFB60D" : "#fff",
              color: filter === "reserved" ? "#fff" : "#000",

            }}
          >
            <Box
              component="img"
              src="/icons/roomReserved.svg"
              alt="roomReserved"
              sx={{ width: "20px", height: "auto" }}
            />
            <div>{t('Reserved')}</div>
          </Button>
          <Button
            onClick={() => { setFilter("occupied"); }}
            sx={{
              ...filterButtonStyle,
              backgroundColor: filter === "occupied" ? "#FF6900" : "#fff",
              color: filter === "occupied" ? "#fff" : "#000",

            }}
          >
            <Box
              component="img"
              src="/icons/roomOccupied.svg"
              alt="roomOccupied"
              sx={{ width: "20px", height: "auto" }}
            />
            <div>{t('Occupied')}</div>
          </Button>
          <Button
            onClick={() => setFilter("")}
            sx={{
              ...filterButtonStyle,
              color: "#000",
              backgroundColor: "#fff",
            }}
          >
            {t('ViewAll')}
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%", }}>
          {filteredRooms.map((room, index) => (
            <Box key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                backgroundColor: "#FFF",
                minHeight: "150px",
                borderRadius: "10px",
                padding: 2,
                boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25);",
                "&:hover": { boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", },
              }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", mb: 1, }}>
                <Typography sx={{ fontSize: "16px", color: "#000", fontWeight: 700, }}>
                  {room.roomName}
                </Typography>
                <Box
                  sx={{
                    borderRadius: "10px",
                    padding: "8px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    backgroundColor: room.availability === "available" ? "#00A63E" : room.availability === "reserved" ? "#FFB60D" : "#FF6900",
                    color: "#fff",
                  }}
                >
                  <Box
                    component="img"
                    src={room.availability === "available" ? "/icons/roomAvailable.svg" : room.availability === "reserved" ? "/icons/roomReserved.svg" : "/icons/roomOccupied.svg"}
                    alt="roompics"
                    sx={{ width: "20px", height: "auto" }}
                  />
                  <div>{room.availability === "available" ? t('Available') : room.availability === "reserved" ? t('Reserved') : t('Occupied')}</div>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", }}>
                <Typography sx={{ fontSize: "14px", color: "#AEAEB2", }}>
                  {room.availability === "occupied" ? t("Occupants") : t("NoOccupants")}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#AEAEB2", }}>
                  {room.availability === "occupied" ? displayOccupants(room) : null}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", mb: 1 }}>
                <Typography sx={{ fontSize: "16px", color: "#4A5565", }}>
                  {room.availability === "occupied" ? t("Date") : null}
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#4A5565", }}>
                  {room.availability === "occupied" ? displaySchedule(room) : null}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", }} onClick={() => handleOpenModal(room._id, "openRoomMenu")}>
                <Typography sx={{ fontSize: "16px", color: "#31BACD", textDecoration: "underline", cursor: "pointer", }}>
                  {t("View")}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    )
  );

};

export default RoomManagementTab;
